import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts';
import VoiceActionItemRecorder from './VoiceActionItemRecorder';

interface ActionItem {
  id: string;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  due_date: string | null;
  created_by: 'ai' | 'user';
  ai_confidence: number | null;
  ai_reasoning: string | null;
  assignee: string | null;
  category: string;
  notes: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface ActionItemsProps {
  transcriptId: string;
}

const ActionItems: React.FC<ActionItemsProps> = ({ transcriptId }) => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState<ActionItem | null>(null);
  const [voicePopulated, setVoicePopulated] = useState(false);
  const [summary, setSummary] = useState<any>(null);

  // Form state
  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    priority: 'low' | 'medium' | 'high' | 'urgent';
    status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
    assignee: string;
    category: string;
    dueDate: string;
    notes: string;
  }>({
    title: '',
    description: '',
    priority: 'medium',
    status: 'pending',
    assignee: '',
    category: 'general',
    dueDate: '',
    notes: ''
  });

  useEffect(() => {
    // Only load action items if user is authenticated and not loading
    if (transcriptId && isAuthenticated && !authLoading && user) {
      loadActionItems();
    } else if (!authLoading && !isAuthenticated) {
      // If not authenticated and not loading, show error
      setError('Authentication required');
      setLoading(false);
    }
  }, [transcriptId, isAuthenticated, authLoading, user]);

  const loadActionItems = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/action-items/list?transcriptId=${transcriptId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Action items API error:', response.status, errorText);
        throw new Error('Failed to load action items');
      }
      
      const data = await response.json() as { actionItems: ActionItem[], summary: any };
      setActionItems(data.actionItems || []);
      setSummary(data.summary || {});
    } catch (err) {
      console.error('Error loading action items:', err);
      setError('Failed to load action items');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveActionItem = async () => {
    try {
      const payload = {
        id: editingItem?.id,
        transcriptId,
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        status: formData.status,
        assignee: formData.assignee || null,
        category: formData.category,
        dueDate: formData.dueDate || null,
        notes: formData.notes || null
      };

      const response = await fetch('/api/action-items/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to save action item');
      }

      // Reset form and close modal
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        status: 'pending',
        assignee: '',
        category: 'general',
        dueDate: '',
        notes: ''
      });
      setShowAddModal(false);
      setEditingItem(null);
      
      // Reload action items
      await loadActionItems();
    } catch (err) {
      console.error('Error saving action item:', err);
      setError('Failed to save action item');
    }
  };

  const handleDeleteActionItem = async (actionItemId: string) => {
    if (!confirm('Are you sure you want to delete this action item?')) {
      return;
    }

    try {
      const response = await fetch(`/api/action-items/delete?id=${actionItemId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete action item');
      }

      await loadActionItems();
    } catch (err) {
      console.error('Error deleting action item:', err);
      setError('Failed to delete action item');
    }
  };

  const handleEditActionItem = (item: ActionItem) => {
    setFormData({
      title: item.title,
      description: item.description,
      priority: item.priority,
      status: item.status,
      category: item.category,
      dueDate: item.due_date || '',
      assignee: item.assignee || '',
      notes: item.notes || ''
    });
    setEditingItem(item);
    setShowAddModal(true);
  };

  const handleVoiceResult = (voiceData: any) => {
    // Auto-populate form with voice-parsed data
    setFormData(prevData => ({
      ...prevData,
      title: voiceData.title || prevData.title,
      description: voiceData.description || prevData.description,
      priority: voiceData.priority || prevData.priority,
      status: voiceData.status || prevData.status,
      category: voiceData.category || prevData.category,
      dueDate: voiceData.dueDate || prevData.dueDate,
      assignee: voiceData.assignee || prevData.assignee,
      notes: voiceData.notes ? 
        (prevData.notes ? `${prevData.notes}\n\nVoice Input: ${voiceData.notes}` : voiceData.notes) 
        : prevData.notes
    }));

    // Set voice populated flag
    setVoicePopulated(true);
    console.log('Voice data populated:', voiceData);
  };

  // Reset form when opening new modal
  const handleOpenNewModal = () => {
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      status: 'pending',
      category: '',
      dueDate: '',
      assignee: '',
      notes: ''
    });
    setVoicePopulated(false);
    setEditingItem(null);
    setShowAddModal(true);
  };

  const handleQuickStatusUpdate = async (actionItemId: string, newStatus: string, notes?: string) => {
    try {
      // Find the current action item to get its data
      const currentItem = actionItems.find(item => item.id === actionItemId);
      if (!currentItem) {
        throw new Error('Action item not found');
      }

      const payload = {
        id: actionItemId,
        transcriptId,
        title: currentItem.title, // Use the actual title from current item
        description: currentItem.description,
        priority: currentItem.priority,
        status: newStatus,
        assignee: currentItem.assignee,
        category: currentItem.category,
        dueDate: currentItem.due_date,
        notes: notes || currentItem.notes
      };

      const response = await fetch('/api/action-items/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json() as { error?: string };
        throw new Error(errorData.error || 'Failed to update action item');
      }

      await loadActionItems();
    } catch (err) {
      console.error('Error updating action item:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to update action item: ${errorMessage}`);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border border-green-200';
      default: return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 border border-green-200';
      case 'in_progress': return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'cancelled': return 'bg-gray-100 text-gray-800 border border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  if (authLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Action Items</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Authenticating...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Action Items</h3>
        </div>
        <div className="flex items-center justify-center py-8 text-red-600">
          <span>Authentication required to load action items</span>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Action Items</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading action items...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Action Items</h3>
          {summary && (
            <p className="text-sm text-gray-600 mt-1">
              {summary.total} total • {summary.pending} pending • {summary.completed} completed
              {summary.ai_generated > 0 && ` • ${summary.ai_generated} AI-detected`}
            </p>
          )}
        </div>
        <button
          onClick={handleOpenNewModal}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          + Add Task
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {actionItems.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <div className="text-5xl mb-4">📋</div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">No action items found</h3>
          <p className="text-sm">AI will automatically detect tasks from your transcript, or you can add them manually.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {actionItems.map((item) => (
            <div key={item.id} className="bg-gray-50 border border-gray-200 rounded-xl p-6 hover:bg-gray-100 transition-all duration-200 hover:shadow-sm">
              {/* Title - Full Width */}
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-gray-900 leading-tight">{item.title}</h4>
              </div>

              {/* AI Reasoning (if AI-created and provides value) */}
              {item.ai_reasoning && item.created_by === 'ai' && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                  <div className="flex items-start gap-2">
                    <span className="text-purple-600 text-sm font-medium">🧠 AI Context:</span>
                    <p className="text-purple-700 text-sm leading-relaxed">{item.ai_reasoning}</p>
                  </div>
                </div>
              )}

              {/* Metadata Row */}
              <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                <div className="flex items-center gap-6">
                  {item.category && (
                    <div className="flex items-center gap-1">
                      <span className="font-medium">Category:</span>
                      <span className="px-2 py-1 bg-gray-200 rounded text-gray-700">{item.category}</span>
                    </div>
                  )}
                  {item.assignee && (
                    <div className="flex items-center gap-1">
                      <span className="font-medium">Assignee:</span>
                      <span>{item.assignee}</span>
                    </div>
                  )}
                  {item.due_date && (
                    <div className="flex items-center gap-1">
                      <span className="font-medium">Due:</span>
                      <span>{new Date(item.due_date).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2 text-gray-400">
                  <span>Created {new Date(item.created_at).toLocaleDateString()}</span>
                  {item.created_by === 'ai' ? (
                    <span 
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200" 
                      title={`AI Confidence: ${Math.round((item.ai_confidence || 0) * 100)}%`}
                    >
                      🤖 AI
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                      👤 User
                    </span>
                  )}
                </div>
              </div>

              {/* Notes Section */}
              {item.notes && (
                <div className="mb-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <span className="text-yellow-600 text-sm font-medium">📝 Notes:</span>
                      <p className="text-yellow-800 text-sm leading-relaxed">{item.notes}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Bar - Priority/Status + Action Buttons */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                {/* Priority and Status Badges */}
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Priority:</span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${getPriorityColor(item.priority)}`}>
                      {item.priority === 'urgent' && '🔴'} 
                      {item.priority === 'high' && '🟠'} 
                      {item.priority === 'medium' && '🟡'} 
                      {item.priority === 'low' && '🟢'} 
                      {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status:</span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(item.status)}`}>
                      {item.status === 'completed' && '✅'} 
                      {item.status === 'in_progress' && '🔄'} 
                      {item.status === 'pending' && '⏳'} 
                      {item.status === 'cancelled' && '❌'} 
                      {item.status.replace('_', ' ').charAt(0).toUpperCase() + item.status.replace('_', ' ').slice(1)}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  {item.status !== 'completed' && (
                    <button
                      onClick={() => handleQuickStatusUpdate(item.id, 'completed')}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                      title="Mark as completed"
                    >
                      ✓ Complete
                    </button>
                  )}
                  <button
                    onClick={() => handleEditActionItem(item)}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteActionItem(item.id)}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {editingItem ? 'Edit Action Item' : 'Add New Action Item'}
              </h3>
              
              {/* Voice Recorder - positioned in header */}
              {!editingItem && (
                <VoiceActionItemRecorder
                  onVoiceResult={handleVoiceResult}
                  transcriptId={transcriptId}
                />
              )}
            </div>
            
            <div className="space-y-4">
              {/* Voice Populated Indicator */}
              {voicePopulated && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-green-800 font-medium">
                      ✨ Voice input auto-populated fields - Review and adjust as needed
                    </span>
                  </div>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter task title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Enter task description"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., follow-up, research"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
                <input
                  type="text"
                  value={formData.assignee}
                  onChange={(e) => setFormData({ ...formData, assignee: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Who should handle this task?"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={2}
                  placeholder="Additional notes or updates"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingItem(null);
                  setVoicePopulated(false);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveActionItem}
                disabled={!formData.title.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {editingItem ? 'Update' : 'Create'} Task
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionItems; 